import React, {memo} from 'react';
import ReactApexChart from 'react-apexcharts';

const parseColor = (color) => {
	if (typeof color !== 'string') {
		return null;
	}

	const normalized = color.trim();

	if (/^#([0-9a-f]{3}){1,2}$/i.test(normalized)) {
		let hex = normalized.slice(1);

		if (hex.length === 3) {
			hex = hex.split('').map((char) => char + char).join('');
		}

		return {
			r: parseInt(hex.slice(0, 2), 16),
			g: parseInt(hex.slice(2, 4), 16),
			b: parseInt(hex.slice(4, 6), 16)
		};
	}

	const rgbMatch = normalized.match(/^rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)$/i);

	if (!rgbMatch) {
		return null;
	}

	return {
		r: parseInt(rgbMatch[1], 10),
		g: parseInt(rgbMatch[2], 10),
		b: parseInt(rgbMatch[3], 10)
	};
};

const toRgba = (color, alpha) => {
	const parsedColor = parseColor(color);

	if (!parsedColor) {
		return color;
	}

	return `rgba(${parsedColor.r}, ${parsedColor.g}, ${parsedColor.b}, ${alpha})`;
};

const groupThousands = (value) => String(value).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');

// Částka ve formátu „1 231,00" – dvě desetinná místa, ať je zobrazení připravené
// i na jinou měnu (eura).
const formatMoney2 = (value) => {
	const n = Number(value) || 0;
	const [intPart, decPart] = Math.abs(n).toFixed(2).split('.');

	return `${n < 0 ? '-' : ''}${groupThousands(intPart)},${decPart}`;
};

const mixWithWhite = (color, whiteRatio = 0.18) => {
	const parsedColor = parseColor(color);

	if (!parsedColor) {
		return color;
	}

	const mixChannel = (channel) => Math.round(channel + (255 - channel) * whiteRatio);

	return `rgb(${mixChannel(parsedColor.r)}, ${mixChannel(parsedColor.g)}, ${mixChannel(parsedColor.b)})`;
};

const mixWithBlack = (color, blackRatio = 0.18) => {
	const parsedColor = parseColor(color);

	if (!parsedColor) {
		return color;
	}

	const mixChannel = (channel) => Math.round(channel * (1 - blackRatio));

	return `rgb(${mixChannel(parsedColor.r)}, ${mixChannel(parsedColor.g)}, ${mixChannel(parsedColor.b)})`;
};

function ChartArea({data1, labels, linesColor = '#bbbbbb', selectedColumn, stepSize = 5000, colorPlus = []}) {
	var cs = {
		name: 'cs',
		options: {
			months: ['Leden', 'Únor', 'Březen', 'Duben', 'Květen', 'Červen', 'Červenec', 'Srpen', 'Září', 'Říjen', 'Listopad', 'Prosinec'],
			shortMonths: ['Led', 'Úno', 'Bře', 'Dub', 'Kvě', 'Čvn', 'Čvc', 'Srp', 'Zář', 'Říj', 'Lis', 'Pro'],
			days: ['Neděle', 'Pondělí', 'Úterý', 'Středa', 'Čtvrtek', 'Pátek', 'Sobota'],
			shortDays: ['Ne', 'Po', 'Út', 'St', 'Čt', 'Pá', 'So'],
			toolbar: {
				exportToSVG: 'Stáhnout SVG',
				exportToPNG: 'Stáhnout PNG',
				exportToCSV: 'Stáhnout CSV',
				menu: 'Menu',
				selection: 'Vybrat',
				selectionZoom: 'Zoom: Vybrat',
				zoomIn: 'Zoom: Přiblížit',
				zoomOut: 'Zoom: Oddálit',
				pan: 'Přesouvat',
				reset: 'Resetovat',
			},
		},
	};

	// Průměr jen z předchozích měsíců – poslední (aktuální, ještě neuzavřený) měsíc se
	// nezapočítává nikdy, ani s kladnou hodnotou, jinak by průměr zkresloval.
	const average1 = (() => {
        const previousValues = data1.slice(0, -1);
        const positiveValues = previousValues.filter(val => parseFloat(val) > 0);
        
        if (positiveValues.length > 0) {
            return positiveValues.reduce((a, b) => a + parseFloat(b), 0) / positiveValues.length;
        }
        
        return 0;
    })();

	const chartGridColor = toRgba(linesColor, 0.26);
	const chartLabelColor = toRgba(linesColor, 0.88);
	const chartTrackColor = 'rgba(255, 255, 255, 0.05)';
	const chartGlowColor = 'rgba(214, 255, 233, 0.08)';
	const latestValueIndex = (() => {
		for (let i = data1.length - 1; i >= 0; i -= 1) {
			const parsedValue = parseFloat(data1[i]);

			if (!Number.isNaN(parsedValue)) {
				return i;
			}
		}

		return -1;
	})();
	const latestValue = latestValueIndex >= 0 ? parseFloat(data1[latestValueIndex]) : 0;
	const latestColor = latestValueIndex >= 0 && colorPlus && colorPlus[latestValueIndex] ? colorPlus[latestValueIndex] : '#66bb66';

	var series = [];

	var series1 = [];
	series1 = data1.map((val, i) => {
		const baseColor = colorPlus && colorPlus[i] ? colorPlus[i] : '#66bb66';
		const numericValue = parseFloat(val);
		const useDarkerVariant = !Number.isNaN(numericValue) && numericValue > latestValue;
		const useLighterVariant = !Number.isNaN(numericValue) && numericValue < latestValue;
		const fillColor = useDarkerVariant ? mixWithBlack(baseColor, 0.18) : useLighterVariant ? mixWithWhite(baseColor, 0.1) : baseColor;

		return {
			x: labels[i],
			y: val,
			fillColor: fillColor,
			strokeColor: useDarkerVariant ? mixWithBlack(fillColor, 0.2) : useLighterVariant ? mixWithWhite(fillColor, 0.2) : mixWithWhite(fillColor, 0.12)
		};
	});

	series.push({name: 'Příjmy', data: series1, typeV: 1});

	let gr = {
		series: series,
		options: {
			annotations: {
                yaxis: [
					...(latestValueIndex >= 0 ? [{
						y: latestValue,
						borderColor: toRgba(latestColor, 0.7),
						strokeDashArray: 5,
						borderWidth: 1,
						opacity: 0.2,
					}] : []),
                    {
                        y: average1,
						borderColor: 'rgba(247, 183, 107, 0.72)',
                        strokeDashArray: 5,
                        borderWidth: 1,
						opacity: 0.5,
                        label: {
							borderColor: 'rgba(247, 183, 107, 0.72)',
                            style: {
								color: '#fdf8ef',
								background: 'rgba(127, 88, 27, 0.92)',
                                fontSize: '1rem',
                                padding: {
                                    left: 2,
                                    right: 2,
                                    top: 2,
                                    bottom: 2,
                                }
                            },
                            text: formatMoney2(average1),
                            position: 'left',
                            offsetX: 25,
                        },
                    },
                ],
            },
			chart: {
				width: '100%',
				height: '100%',
				offsetY: -10,
				offsetX: -5,
				parentHeightOffset: 0,
				locales: [cs],
				defaultLocale: 'cs',
				type: 'bar',
				zoom: {enabled: false},
				stacked: false,
				toolbar: {show: false},
				dropShadow: {
					enabled: true,
					top: 8,
					left: 0,
					blur: 14,
					color: chartGlowColor,
					opacity: 0.4,
				},
				events: {
					dataPointSelection: function (event, chartContext, config) {
						let selectedDataIndex = config.dataPointIndex;
						let selectedSeriesIndex = config.seriesIndex;
						let selectedYValue = config.w.config.series[selectedSeriesIndex].data[selectedDataIndex];
						let selectedType = config.w.config.series[selectedSeriesIndex].typeV;
						let selectedLabel = config.w.globals.labels[selectedDataIndex];

						let date = new Date(selectedLabel);
						let selectedLabelS = date.getMonth() + 1 + '.' + date.getFullYear();
						selectedLabelS = '.' + selectedLabelS;

						selectedColumn(selectedType, selectedYValue, selectedLabelS);
					},
				},
			},
			plotOptions: {
				bar: {
					horizontal: false,
					borderRadius: 1,
					borderRadiusApplication: 'end',
					columnWidth: '64%',
					endingShape: 'flat',
					colors: {
						backgroundBarColors: [chartTrackColor],
						backgroundBarOpacity: 1,
						backgroundBarRadius: 1,
					},
				}
			},
			dataLabels: {
				enabled: false,
				offsetY: -20,
				style: {
					fontSize: '0.9rem',
					colors: [chartLabelColor],
				},
			},
			stroke: {
				show: true,
				curve: 'smooth',
				lineCap: 'round',
				width: 1,
				colors: ['rgba(255, 255, 255, 0.28)']
			},
			colors: ['#66bb66'],
			xaxis: {
				axisTicks: {show: false, tickAmount: 4},
				axisBorder: {show: false},
				labels: {
					formatter: function (value) {
						let date = new Date(value);
                        if (isNaN(date.getTime())) return value;
                        return date.getMonth() + 1 + '.' + date.getFullYear();
					},
					show: false,
					rotate: 0,
					style: {fontSize: '0.9rem', colors: chartLabelColor},
					offsetY: -4,
				},
			},
			yaxis: {
				stepSize: stepSize,
				labels: {
					formatter: function (value) {
						return formatMoney2(value);
					},
					show: true,
					rotate: 0,
					offsetX: -6,
					style: {fontSize: '1rem', colors: [chartLabelColor]},
				},
			},
			fill: {
				opacity: 0.96,
				type: 'gradient',
				gradient: {
					shade: 'dark',
					type: 'vertical',
					shadeIntensity: 0.38,
					inverseColors: false,
					opacityFrom: 0.98,
					opacityTo: 0.72,
					stops: [0, 100],
				}
			},
			tooltip: {
				theme: 'dark',
				fillSeriesColor: false,
				style: {
					fontSize: '1.1rem'
				},
				y: {
					formatter: function (value) {
						return formatMoney2(value);
					}
				}
			},
			title: {text: '', align: 'left'},
			subtitle: {text: '', align: 'left'},
			labels: labels,
			legend: {show: false, labels: {colors: chartLabelColor, useSeriesColors: false}},
			states: {
				hover: {
					filter: {
						type: 'lighten',
						value: 0.12
					}
				},
				active: {
					filter: {
						type: 'darken',
						value: 0.04
					}
				}
			},
			grid: {
				show: true,
				position: 'back',
				borderColor: chartGridColor,
				strokeDashArray: 7,
				padding: {
					top: -10,
					right: 0,
					bottom: -12,
					left: 0,
				},
				xaxis: {lines: {show: false}},
				yaxis: {lines: {show: true}},
			},
		},
	};

	return <ReactApexChart options={gr.options} series={gr.series} type="bar" width="100%" height="100%" />;
}

export default memo(ChartArea);
